import {
  User as FirestoreUser,
  applyActionCode,
  sendEmailVerification as authSendEmailVerification,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import {
  ApiFunctions,
  ConnectApi,
  ConnectLinkWithReferralHash,
  ConnectPatientConsent,
  EmailUser,
  FirebaseAuthErrorCode,
  User as HydraDataTypesUser,
  RegisterConnectUser,
  RegisterExistingConnectUser,
  RegisterUser,
} from 'hydra-data-types';
import { Analytics } from '../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../helpers/analytics/AnalyticsEvent';
import { ConnectInitializeUser } from '../store/StoreTypes';
import { ApiClient } from './ApiClient';
import { ApiClientNerva } from './NervaApiClient';

export type FirebaseAuthError = {
  code: string;
};

export type AuthFormError = { email: string } | { password: string };

const FORM_ERRORS: { [code in FirebaseAuthErrorCode | string]: AuthFormError } = {
  // From the RN Firebase Auth lib?
  'auth/user-not-found': { email: 'Email does not exist' },
  'auth/user-disabled': { email: 'User disabled' },
  'auth/invalid-email': { email: 'This email is not valid' },
  'auth/email-already-in-use': { email: 'Email already in use' },
  'auth/wrong-password': { password: 'Wrong password' },
  'auth/weak-password': {
    password: 'Weak password, try a more complicated one',
  },
  // auth/missing-android-pkg-name
  // auth/missing-continue-uri
  // auth/missing-ios-bundle-id
  // auth/invalid-continue-uri
  // auth/unauthorized-continue-uri

  // From the Firebase admin API - These codes can be passed back from Mindset API calls
  'auth/email-already-exists': { email: 'Email already in use' },
  'auth/invalid-password': {
    password: 'The password must be a string with at least 6 characters',
  },
};

export async function registerHcp(registerUser: RegisterConnectUser): Promise<EmailUser> {
  try {
    const { user } = await new ApiClient().register({ user: registerUser });

    await signInWithEmailAndPassword(getAuth(), user.email, registerUser.password);

    Analytics.identify({ ...user, hcpProduct: true });

    await sendEmailVerification();

    return user;
  } catch (error) {
    throw new Error(`Registration (HCP) failed : ${error}`);
  }
}

export async function registerPatient(registerUser: RegisterUser): Promise<EmailUser> {
  try {
    const { user } = await new ApiClientNerva().register({
      user: registerUser,
    });

    await signInWithEmailAndPassword(getAuth(), user.email, registerUser.password);

    Analytics.identifyPatient({ ...user, hcpProduct: true });

    return user;
  } catch (error) {
    throw new Error(`Registration (patient) failed : ${error}`);
  }
}

export async function registerExistingUser(
  registerUser: RegisterExistingConnectUser & { password: string }
): Promise<EmailUser> {
  const { password, ...existingUser } = registerUser;

  const { user } = await new ApiClient().registerExistingUser({
    user: existingUser,
  });

  if (password !== undefined) {
    await signInWithEmailAndPassword(getAuth(), user.email, password);
  }

  await sendEmailVerification();

  Analytics.identify({ ...user, hcpProduct: false });

  return user;
}

export async function sendEmailVerification() {
  try {
    const currentFirebaseUser = getAuth().currentUser;
    if (currentFirebaseUser) {
      await authSendEmailVerification(currentFirebaseUser, {
        url: `${process.env.REACT_APP_BASE_URL}` ?? '',
        handleCodeInApp: true,
      });
    }
  } catch (error) {
    throw new Error(`Email verification could not be sent: ${error}`);
  }
}

export async function initialiseHcpUser(): Promise<ConnectInitializeUser> {
  const { user } = await new ApiClient().initialize();
  return user;
}

export async function signIn(email: string, password: string): Promise<FirestoreUser | null> {
  const { user } = await signInWithEmailAndPassword(getAuth(), email, password);
  return user;
}

export async function doesUserExist(
  email: ApiFunctions.DoesUserExistData
): Promise<ApiFunctions.DoesUserExistResult> {
  const doesUserExistResult = await new ApiClient().doesUserExist(email);
  return doesUserExistResult;
}

type ConnectUserAndProfile = ConnectApi.ConnectUserAndProfile;
export async function createHcpPatientLink(
  hcpLink: ConnectLinkWithReferralHash
): Promise<ConnectUserAndProfile> {
  try {
    const linkPatientToHcpResponse = await new ApiClient().linkPatientToHcp(hcpLink);
    return linkPatientToHcpResponse.hcp;
  } catch (error) {
    throw new Error(`Could not link to hcp ${error}`);
  }
}

export async function updatePatientConsent(consent: ConnectPatientConsent): Promise<void> {
  try {
    await new ApiClient().confirmLink(consent);
  } catch (error) {
    throw new Error(`Could not update consent information: ${error}`);
  }
}

export async function getAuthorisationKey(): Promise<string | undefined> {
  try {
    const { token } = await new ApiClient().getAuthToken();
    Analytics.track({ event: AnalyticsEventName.a_sign_token_succeeded });
    return token;
  } catch (error) {
    Analytics.track({
      event: AnalyticsEventName.a_signin_token_failed,
      error: String(error),
    });
    throw new Error(`Could not get auth token: ${error}`);
  }
}

export async function applyEmailVerificationCode(code: string): Promise<FirestoreUser | null> {
  const auth = getAuth();
  await applyActionCode(auth, code);
  await auth.currentUser?.reload();
  return auth.currentUser;
}

export async function isUserPaid(userId: string): Promise<boolean> {
  const db = getFirestore();
  const userDocRef = doc(db, 'users', userId);
  const userDocSnap = await getDoc(userDocRef);
  const userData = userDocSnap.data() as HydraDataTypesUser | undefined;
  return Boolean(userData?.paid);
}

export function makeFormErrors(
  e: FirebaseAuthError | { details: FirebaseAuthError } | any
): AuthFormError | undefined {
  return FORM_ERRORS[e.code] ?? FORM_ERRORS[e.details?.code];
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(getAuth(), email);
}
